import { ref, readonly, type DeepReadonly, type Ref } from 'vue';

const STORAGE_KEY = 'sidebar-collapsed';
const MOBILE_BREAKPOINT = 768;

// Module-level singleton state — shared across all consumers
const isCollapsed = ref(false);
const isMobile = ref(false);
const isOverlayOpen = ref(false);

interface UseSidebarStateReturn {
  isCollapsed: DeepReadonly<Ref<boolean>>
  isMobile: DeepReadonly<Ref<boolean>>
  isOverlayOpen: DeepReadonly<Ref<boolean>>
  toggle: () => void
  closeOverlay: () => void
  init: () => void
  setupMediaListener: () => void
  teardownMediaListener: () => void
}

export function useSidebarState(): UseSidebarStateReturn {
  function init() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      isCollapsed.value = stored === 'true';
    }
    checkMobile();
  }

  function toggle() {
    if (isMobile.value) {
      isOverlayOpen.value = !isOverlayOpen.value;
    } else {
      isCollapsed.value = !isCollapsed.value;
      try {
        localStorage.setItem(STORAGE_KEY, String(isCollapsed.value));
      } catch {
        // Private browsing or quota exceeded — state still works in-memory
      }
    }
  }

  function closeOverlay() {
    isOverlayOpen.value = false;
  }

  function checkMobile() {
    isMobile.value = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
    if (isMobile.value) {
      isOverlayOpen.value = false;
    }
  }

  let mediaQuery: MediaQueryList | null = null;

  function setupMediaListener() {
    mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    mediaQuery.addEventListener('change', checkMobile);
  }

  function teardownMediaListener() {
    mediaQuery?.removeEventListener('change', checkMobile);
  }

  return {
    isCollapsed: readonly(isCollapsed),
    isMobile: readonly(isMobile),
    isOverlayOpen: readonly(isOverlayOpen),
    toggle,
    closeOverlay,
    init,
    setupMediaListener,
    teardownMediaListener,
  };
}
